import React, { useState } from 'react';
import { motion } from 'framer-motion';
import WeatherWidget from '../components/WeatherWidget';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCloud, FiDroplet, FiWind, FiSun, FiThermometer } = FiIcons;

function Weather() {
  const [mapType, setMapType] = useState('precipitation');
  const [timeFrame, setTimeFrame] = useState('now');

  const mapTypes = [
    { id: 'precipitation', name: 'Precipitation', icon: FiCloud },
    { id: 'temperature', name: 'Temperature', icon: FiThermometer },
    { id: 'wind', name: 'Wind Speed', icon: FiWind },
    { id: 'clouds', name: 'Cloud Cover', icon: FiCloud },
    { id: 'pressure', name: 'Pressure', icon: FiDroplet },
    { id: 'air_quality', name: 'Air Quality', icon: FiWind }
  ];

  const timeFrames = [
    { id: 'now', name: 'Now' },
    { id: '3h', name: '3 Hours' },
    { id: '6h', name: '6 Hours' },
    { id: '12h', name: '12 Hours' },
    { id: '24h', name: '24 Hours' },
    { id: '48h', name: '2 Days' }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section with NYC Weather Background */}
      <section 
        className="relative h-[40vh] flex items-center justify-center"
        style={{
          backgroundImage: 'url("https://images.unsplash.com/photo-1499092346589-b9b6be3e94b2?q=80&w=2071&auto=format&fit=crop")',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-dark-bg"></div>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative z-10 text-center px-4"
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white">
            NYC Weather Radar & Forecast
          </h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto">
            Real-time weather conditions and forecasts across all five boroughs
          </p>
        </motion.div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Interactive Radar Map Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-12 bg-dark-card rounded-lg p-6 border border-dark-border"
        >
          <div className="mb-6">
            <h2 className="text-2xl font-bold mb-4 gradient-text">Interactive Weather Radar</h2>
            
            {/* Map Type Controls */}
            <div className="flex flex-wrap gap-2 mb-4">
              {mapTypes.map((type) => (
                <button
                  key={type.id}
                  onClick={() => setMapType(type.id)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-full transition-colors ${
                    mapType === type.id
                      ? 'bg-accent-blue text-white'
                      : 'bg-dark-bg text-gray-400 hover:text-white hover:bg-accent-blue/20'
                  }`}
                >
                  <SafeIcon icon={type.icon} />
                  <span>{type.name}</span>
                </button>
              ))}
            </div>

            {/* Time Frame Controls */}
            <div className="flex flex-wrap gap-2">
              {timeFrames.map((time) => (
                <button
                  key={time.id}
                  onClick={() => setTimeFrame(time.id)}
                  className={`px-4 py-2 rounded-full transition-colors ${
                    timeFrame === time.id
                      ? 'bg-primary-blue text-white'
                      : 'bg-dark-bg text-gray-400 hover:text-white hover:bg-primary-blue/20'
                  }`}
                >
                  {time.name}
                </button>
              ))}
            </div>
          </div>

          {/* Radar Map */}
          <div className="aspect-[16/9] w-full rounded-lg overflow-hidden bg-dark-bg">
            <iframe
              src={`https://embed.windy.com/embed2.html?lat=40.7128&lon=-74.0060&zoom=10&level=surface&overlay=${mapType}&menu=&message=&marker=&calendar=&pressure=&type=map&location=coordinates&detail=&detailLat=40.7128&detailLon=-74.0060&metricWind=default&metricTemp=%C2%B0F&radarRange=-1`}
              width="100%"
              height="100%"
              frameBorder="0"
              title="NYC Weather Radar"
            ></iframe>
          </div>
        </motion.div>

        {/* Weather Widget */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <WeatherWidget />
        </motion.div>
      </div>
    </div>
  );
}

export default Weather;